import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { autoinject } from "aurelia-framework";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { SaveCustomerRequest, SaveBusinessUnitRequest, GetCustomersResponse, GetBusinessUnitsResponse, GetBusinessUnitIdsResponse, GetBusinessUnitRuleParametersResponse, GetCustomersRuleParametersResponse, GetCustomerResponse, GetCustomerBusinessUnitIdsResponse, SaveCustomerResponse, CustomerModel, GetCustomerAllowedDomainsResponse, UpdateCustomerAllowedDomainsRequest, UpdateCustomerAllowedDomainsResponse, CloneCustomerRoleRequest, UpdateBusinessUnitAllowedDomainsRequest, UpdateBusinessUnitAllowedDomainsResponse } from "../../models/customers/customer.models";
import { Urls } from "../../constants/modules/customer.constants";
import { BaseValidationResponse } from "../../models/validations/validations.models";
import { DeleteResponse } from "../../models/basics/basics.models";
import { RoleModel } from "../../models/manage/roles.models";
import { RequestConstants } from "@rightship-tech/common-ui/constants/request.constants";
import { FileHelper } from "../../helpers/file-helper";
import { Reflection } from '../../utilities/reflection';
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';
import { ConfigurationService } from '@rightship-tech/common-ui/services/basic/configuration.service';


@autoinject()
export class CustomerService {
    private _getCustomerPromiseCache: { [index: number]: Promise<GetCustomerResponse> } = {};
    private _getBusinessUnitPromiseCache: { [index: number]: Promise<GetBusinessUnitsResponse> } = {};

    constructor(private requestClient: RequestClientService, private configurationService: ConfigurationService) { }

    public getCustomers(ids: number[]): Promise<GetCustomersResponse> {
        return this.requestClient.get<GetCustomersResponse>(Urls.GetCustomers, { ids });
    }

    public getCustomerRuleParameters(id: number): Promise<GetCustomersRuleParametersResponse> {
        return this.requestClient.get<GetCustomersRuleParametersResponse>(`${Urls.GetCustomerRuleParameters}/${id}`, {});
    }

    public getBusinessUnitRuleParameters(id: number): Promise<GetBusinessUnitRuleParametersResponse> {
        return this.requestClient.get<GetBusinessUnitRuleParametersResponse>(`${Urls.GetBusinessUnitRuleParameters}/${id}`, {});
    }

    public getBusinessUnits(ids: number[]): Promise<GetBusinessUnitsResponse> {
        return this.requestClient.get<GetBusinessUnitsResponse>(Urls.GetBusinessUnits, { ids });
    }

    async clearCustomerCache(id: number) {
        this._getCustomerPromiseCache[id] = null;
    }

    getReadOnlyIf(vetTaskId: number) {
        return this.requestClient.get<string>(`${Urls.GetReadOnlyIf}/${vetTaskId}`, {});
    }

    getDisplayResultJson(vetTaskId: number): Promise<string> {
        return this.requestClient.get<string>(`${Urls.GetDisplayResultJson}/${vetTaskId}`, {});
    }

    getCurrentCustomer(): Promise<GetCustomersResponse> {
        return this.requestClient.get<GetCustomersResponse>(Urls.GetCurrentCustomer, {});
    }

    getCustomerBusinessUnitIds(customerId: number): Promise<GetCustomerBusinessUnitIdsResponse> {
        return this.requestClient.get<GetCustomerBusinessUnitIdsResponse>(`${Urls.GetCustomerBusinessUnitIds}/${customerId}`, {})
    }

    async getCustomer(id: number): Promise<GetCustomerResponse> {
        if (this._getCustomerPromiseCache[id]) {
            return await this._getCustomerPromiseCache[id];
        }

        this._getCustomerPromiseCache[id] = this.requestClient.get<GetCustomerResponse>(Urls.GetCustomer, { id });

        return await this._getCustomerPromiseCache[id];
    }

    async getBusinessUnit(id: number) {

        if (this._getBusinessUnitPromiseCache[id]) {
            return CloneHelper.clone(await this._getBusinessUnitPromiseCache[id]);
        }

        this._getBusinessUnitPromiseCache[id] = this.getBusinessUnits([id]);

        return CloneHelper.clone(await this._getBusinessUnitPromiseCache[id]);
    }

    async canContactShipManagersDirectly(businessUnitId: number): Promise<boolean> {
        return this.requestClient.get(`${Urls.CanContactShipManagersDirectly}/${businessUnitId}`);
    }

    public isHostedCustomer(): Promise<boolean> {
        return this.requestClient.get(Urls.IsHostedCustomer, {});
    }

    public saveCustomer(request: SaveCustomerRequest): Promise<BaseValidationResponse> {
        return new Promise<BaseValidationResponse>((resolve, reject) => {
            this.requestClient.post(Urls.SaveCustomer, { id: request.id }, request).then((result: SaveCustomerResponse) => {
                if (result.validationResult && result.validationResult.hasError) {
                    reject(result);
                } else {
                    this.clearCustomerCache(request.id).then(() => {
                        resolve(result);
                    });
                    const clearBuCache = Object.keys(request.changes).indexOf(Reflection.getPropertyName<CustomerModel>(x => x.isActive)) > -1;
                    if (clearBuCache) {
                        this.clearCacheForBusinessUnits(result.businessUnitIds);
                    }
                }
            }, (e) => {
                reject(e);
            });
        });
    }

    public saveBusinessUnit(request: SaveBusinessUnitRequest): Promise<BaseValidationResponse> {
        return new Promise<BaseValidationResponse>((resolve, reject) => {
            this.requestClient.post(
                Urls.SaveBusinessUnit,
                { id: request.id },
                request).then((result: BaseValidationResponse) => {
                    if (result.validationResult && result.validationResult.hasError) {
                        reject(result);
                    } else {
                        resolve(result);
                    }
                }, (e) => {
                    reject(e);
                });
        });
    }

    public deleteCustomer(id: number): Promise<DeleteResponse> {
        return this.requestClient.post(
            Urls.DeleteCustomer,
            { id: id });
    }

    public deleteBusinessUnit(id: number): Promise<DeleteResponse> {
        return this.requestClient.post(
            Urls.DeleteBusinessUnit,
            { id: id });
    }

    public saveRole(role: RoleModel, branchId: number): Promise<BaseValidationResponse> {
        return new Promise<BaseValidationResponse>((resolve, reject) => {
            this.requestClient.post(
                Urls.SaveRole,
                {},
                { role, branchId }).then((result: BaseValidationResponse) => {
                    if (result.validationResult && result.validationResult.hasError) {
                        reject(result);
                    } else {
                        resolve(result);
                    }
                }, (e) => {
                    reject(e);
                });
        });
    }

    public cloneRole(request: CloneCustomerRoleRequest): Promise<BaseValidationResponse> {
        return new Promise<BaseValidationResponse>((resolve, reject) => {
            this.requestClient.post(Urls.CloneRole, {}, request).then((result: BaseValidationResponse) => {
                    if (result.validationResult && result.validationResult.hasError) {
                        reject(result);
                    } else {
                        resolve(result);
                    }
                }, (e) => {
                    reject(e);
                });
        });
    }

    public getAllAccessableBusinessUnitsByType(entityTypeId?: number): Promise<GetBusinessUnitIdsResponse> {
        return this.requestClient.post(
            Urls.GetAllAccessableBusinessUnitsByType,
            { entityTypeId: entityTypeId });
    }

    public clearBusinessUnitPromiseCache(id: number) {
        if (this._getBusinessUnitPromiseCache[id]) {
            this._getBusinessUnitPromiseCache[id] = null;
        }
    }

    public clearCustomerPromiseCache(id: number) {
        if (this._getCustomerPromiseCache[id]) {
            this._getCustomerPromiseCache[id] = null;
        }
    }

    public clearCacheForBusinessUnits(ids: number[]) {
        if (!ids || ids.length == 0) return;

        ids.forEach(id => {
            this.clearBusinessUnitPromiseCache(id);
        });
    }

    public clearCustomerAndBusinessUnitCache(customerId: number, businessUnitIds: number[]) {
        this.clearCustomerPromiseCache(customerId);
        this.clearCacheForBusinessUnits(businessUnitIds);
    }

    public downloadRulesExcel(customerId: number, businessUnitId?: number, entityName?: string) {
        return new Promise<void>((resolve, reject) => {
            try {
                let url = `${ConfigService.instance().config.urls.rightShipPlatformApiUrl}/api/v1/Customer/DownloadRulesAsExcel?customerId=${customerId}`;
                if (businessUnitId)
                    url = url + "&businessUnitId=" + businessUnitId;

                $.ajax({
                    type: "GET",
                    url: url,
                    xhrFields: {
                        withCredentials: true
                    },
                    beforeSend: (request) => {
                        const headers = this.configurationService.getList(RequestConstants.ConfigurationTypes.RequestHeaders);
                        const keys = Object.keys(headers);
                        keys.forEach(key => {
                            request.setRequestHeader(key, headers[key].toString());
                        });
                    },
                    success: x => {
                        const url = window.URL.createObjectURL(x);
                        FileHelper.downloadFile(url, (entityName != null && entityName.trim() != "" ? entityName + ' ' : '') + 'Rules Documentation.xlsx');

                        setTimeout(() => {
                            window.URL.revokeObjectURL(url);
                            resolve();
                        });
                    },
                    error: () => {
                        reject();
                    },
                    xhr: () => {
                        const xhr = new XMLHttpRequest();
                        xhr.responseType = 'blob'
                        return xhr;
                    }
                });
            }
            catch (exception) {
                resolve();
            }
        });
    }

    public sendBuyFullVersionRequest(customerId: number) {
        return new Promise<void>((resolve, reject) => {
            this.requestClient.get(Urls.SendBuyFullVersionRequest, { customerId: customerId })
                .then(() => {
                    resolve();
                }, (e) => {
                    reject(e);
                });
        });
    }

    public getCustomerAllowedDomains(customerId: number): Promise<GetCustomerAllowedDomainsResponse> {
        return this.requestClient.get<GetCustomerAllowedDomainsResponse>(Urls.GetCustomerAllowedDomains, { customerId });
    }

    public updateCustomerAllowedDomains(request: UpdateCustomerAllowedDomainsRequest): Promise<UpdateCustomerAllowedDomainsResponse> {
        return this.requestClient.post<UpdateCustomerAllowedDomainsResponse>(Urls.UpdateCustomerAllowedDomains, {}, request);
    }

    public getBusinessUnitAllowedDomains(businessUnitId: number): Promise<GetCustomerAllowedDomainsResponse> {
        return this.requestClient.get<GetCustomerAllowedDomainsResponse>(Urls.GetBusinessUnitAllowedDomains, { businessUnitId });
    }

    public updateBusinessUnitAllowedDomains(request: UpdateBusinessUnitAllowedDomainsRequest): Promise<UpdateBusinessUnitAllowedDomainsResponse> {
        return this.requestClient.post<UpdateCustomerAllowedDomainsResponse>(Urls.UpdateBusinessUnitAllowedDomains, {}, request);
    }
}